import { useMemo, useState, type ChangeEvent } from 'react'
import { Link } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { useImportContacts, type ContactImportResult } from '../api/contacts'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { guessColumns, parseCsv, toImportRows, type ColumnMap, type ParsedCsv } from '../lib/csv'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

const none = 'none'
const maxRows = 5000

// CSV import: the browser parses the file, the user confirms which columns
// are which and whether these customers opted in, the API does the rest.
export function ContactsImportPage() {
  const me = useMe()
  const [csv, setCsv] = useState<ParsedCsv | null>(null)
  const [fileName, setFileName] = useState('')
  const [cols, setCols] = useState<ColumnMap>({ phone: -1, name: -1, tags: -1 })
  const [optedIn, setOptedIn] = useState(false)
  const [proof, setProof] = useState('')
  const [result, setResult] = useState<ContactImportResult | null>(null)
  const importRows = useImportContacts()
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'

  const load = (text: string, name: string) => {
    const parsed = parseCsv(text)
    setCsv(parsed)
    setFileName(name)
    setCols(guessColumns(parsed.headers))
    setResult(null)
    importRows.reset()
  }
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    void f.text().then((t) => load(t, f.name))
  }

  const rows = useMemo(() => (csv && cols.phone >= 0 ? toImportRows(csv.rows, cols) : []), [csv, cols])
  const tooMany = rows.length > maxRows
  const ready = rows.length > 0 && !tooMany && (!optedIn || proof.trim() !== '')

  if (me.data && !admin) {
    return (
      <main className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-6">
        <p className="rounded-lg bg-muted p-4 text-sm">Only owners and admins can import contacts.</p>
        <Button asChild variant="ghost" className="w-fit"><Link to="/contacts"><ArrowLeft className="size-4" aria-hidden="true" />Contacts</Link></Button>
      </main>
    )
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <Button asChild variant="ghost" size="sm" className="w-fit"><Link to="/contacts"><ArrowLeft className="size-4" aria-hidden="true" />Contacts</Link></Button>
        <h1 className="text-2xl font-bold">Import contacts</h1>
        <p className="text-sm text-muted-foreground">
          A CSV with a header row. Ten-digit numbers are read as Indian; other countries need the country code. A number already in your contacts is updated, not duplicated. Tags in the file are created when missing.
        </p>
      </div>

      <section className="flex flex-col gap-3 rounded-xl border bg-card p-4">
        <Label htmlFor="csv-file">1. Choose the file</Label>
        <Input id="csv-file" type="file" accept=".csv,text/csv" onChange={onFile} />
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">Or paste the rows</summary>
          <Textarea className="mt-2" rows={4} placeholder={'phone,name,tags\n+91 98765 43210,Asha,VIP;Diwali'} onChange={(e) => load(e.target.value, 'pasted rows')} />
        </details>
        {csv && (
          <p className="text-sm text-muted-foreground">
            {fileName}: {csv.rows.length} {csv.rows.length === 1 ? 'row' : 'rows'}, columns {csv.headers.map((h) => `"${h}"`).join(', ') || 'none'}.
          </p>
        )}
      </section>

      {csv && csv.headers.length > 0 && (
        <section className="flex flex-col gap-3 rounded-xl border bg-card p-4">
          <h2 className="text-sm font-semibold">2. Which column is which</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <ColumnPick label="Phone (required)" value={cols.phone} headers={csv.headers} onChange={(v) => setCols({ ...cols, phone: v })} required />
            <ColumnPick label="Name" value={cols.name} headers={csv.headers} onChange={(v) => setCols({ ...cols, name: v })} />
            <ColumnPick label="Tags (split on ; or |)" value={cols.tags} headers={csv.headers} onChange={(v) => setCols({ ...cols, tags: v })} />
          </div>
          {cols.phone < 0 && <p className="text-sm text-destructive">Pick the column that holds the phone number.</p>}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">The first rows as they will be imported</caption>
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-3">Phone</th><th className="py-1 pr-3">Name</th><th className="py-1">Tags</th></tr></thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t"><td className="py-1 pr-3">{r.phone}</td><td className="py-1 pr-3">{r.name ?? ''}</td><td className="py-1">{r.tags?.join(', ') ?? ''}</td></tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 5 && <p className="mt-1 text-xs text-muted-foreground">and {rows.length - 5} more.</p>}
            </div>
          )}
          {tooMany && <p className="text-sm text-destructive">The file has more than {maxRows} rows. Split it and import each part.</p>}
        </section>
      )}

      {rows.length > 0 && (
        <section className="flex flex-col gap-3 rounded-xl border bg-card p-4">
          <h2 className="text-sm font-semibold">3. Consent</h2>
          <div className="flex items-start gap-2">
            <Checkbox id="opted-in" checked={optedIn} onCheckedChange={(v) => setOptedIn(v === true)} className="mt-0.5" />
            <Label htmlFor="opted-in" className="text-sm leading-snug">
              Every customer in this file agreed to receive WhatsApp messages from us. Meta requires this before a campaign reaches them.
            </Label>
          </div>
          {optedIn && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-proof">How did they agree?</Label>
              <Input id="import-proof" maxLength={500} placeholder="Order form checkbox, Shopify opt-in export, 1 Nov 2026" value={proof} onChange={(e) => setProof(e.target.value)} />
            </div>
          )}
          {!optedIn && <p className="text-xs text-muted-foreground">Without this, contacts are imported with no consent recorded. You can record it per contact later.</p>}
          <div className="flex items-center gap-3">
            <Button disabled={!ready || importRows.isPending} onClick={() => importRows.mutate({ rows, optedIn, ...(optedIn ? { optInProof: proof.trim() } : {}) }, { onSuccess: setResult })}>
              {importRows.isPending ? 'Importing' : `Import ${rows.length} ${rows.length === 1 ? 'contact' : 'contacts'}`}
            </Button>
          </div>
          {importRows.error && <p role="alert" className="text-sm text-destructive">{(importRows.error as RequestFailed).message}</p>}
        </section>
      )}

      {result && (
        <section role="status" className="flex flex-col gap-2 rounded-xl border bg-card p-4">
          <h2 className="text-sm font-semibold">Done</h2>
          <p className="text-sm">{result.created} new, {result.updated} updated, {result.skipped.length} skipped.</p>
          {result.skipped.length > 0 && (
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {result.skipped.slice(0, 50).map((s) => <li key={s.row}>Row {s.row} ({s.phone || 'empty'}): {s.reason}</li>)}
              {result.skipped.length > 50 && <li>and {result.skipped.length - 50} more.</li>}
            </ul>
          )}
          <Button asChild variant="outline" className="w-fit"><Link to="/contacts">See contacts</Link></Button>
        </section>
      )}
    </main>
  )
}

function ColumnPick({ label, value, headers, onChange, required }: { label: string; value: number; headers: string[]; onChange: (v: number) => void; required?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Select value={value < 0 ? none : String(value)} onValueChange={(v) => onChange(v === none ? -1 : Number(v))}>
        <SelectTrigger aria-label={label}><SelectValue /></SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value={none}>Not in the file</SelectItem>}
          {required && value < 0 && <SelectItem value={none}>Choose a column</SelectItem>}
          {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}
